import React, { useEffect, useState, useCallback } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { Link, useSearchParams } from "react-router-dom";
import { Inbox, ArrowDownLeft, ArrowUpRight, MessageCircle, Package, Star } from "lucide-react";
import MerchantPhotoThumb from "../components/MerchantPhotoThumb";
import StarRating, { Stars } from "../components/StarRating";
import RateModal from "../components/RateModal";

const STATUS_LABEL = {
  pending: "قيد الانتظار",
  confirmed: "مؤكد",
  completed: "مكتمل",
  cancelled: "ملغي",
};
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const TabButton = ({ tab, value, icon: Icon, label, count, onSelect }) => (
  <button
    onClick={() => onSelect(value)}
    data-testid={`tab-${value}`}
    className={`flex-1 h-12 rounded-lg flex items-center justify-center gap-2 font-bold ${
      tab === value ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#09637E]"
    }`}
  >
    <Icon size={18} /> {label}
    <span className={`text-xs px-2 py-0.5 rounded-full ${tab === value ? "bg-white/20" : "bg-white"}`}>{count}</span>
  </button>
);

const Reservations = () => {
  const { user } = useAuth();
  const storeId = user?.merchant_id || user?.id; // employees act on behalf of their store
  const isEmployee = user?.role === "employee";
  const [params] = useSearchParams();
  const focusId = params.get("r"); // deep link from notifications → highlight that reservation
  const { markReservationsRead } = useNotifications();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("incoming"); // incoming | outgoing
  const [rateTarget, setRateTarget] = useState(null); // reservation to rate

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reservations");
      setReservations(r.data || []);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); markReservationsRead(); }, [load, markReservationsRead]);
  useEffect(() => {
    if (loading || !focusId) return;
    const target = reservations.find((r) => r.id === focusId);
    if (!target) return;
    setTab(target.owner_id === storeId ? "incoming" : "outgoing");
    setTimeout(() => document.getElementById(`reservation-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  }, [loading, focusId, reservations, storeId]);

  const filtered = reservations.filter((r) =>
    tab === "incoming" ? r.owner_id === storeId : r.requester_id === storeId
  );

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}/status`, { status });
      toast.success("تم التحديث");
      load();
    } catch (e) { toast.error(arabicError(e)); }
  };

  const incomingCount = reservations.filter((r) => r.owner_id === storeId).length;
  const outgoingCount = reservations.filter((r) => r.requester_id === storeId).length;

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2">
          <Inbox className="text-[#09637E]" size={32} /> الحجوزات
        </h1>
        <p className="text-[#666666] mt-1">{isEmployee ? `حجوزات متجر ${user?.store_name || ""} — الواردة (بيع) والصادرة (شراء)` : "جميع الحجوزات الواردة والصادرة"}</p>
      </div>

      <div className="flex gap-2">
        <TabButton tab={tab} value="incoming" icon={ArrowDownLeft} label={isEmployee ? "واردة للمتجر" : "واردة إليّ"} count={incomingCount} onSelect={setTab} />
        <TabButton tab={tab} value="outgoing" icon={ArrowUpRight} label={isEmployee ? "طلبات المتجر" : "طلباتي"} count={outgoingCount} onSelect={setTab} />
      </div>

      {loading ? (
        <div className="text-center text-[#666666] py-12">جارٍ التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-2xl p-12 text-center" data-testid="empty-reservations">
          <Package className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] font-bold text-lg">
            {tab === "incoming" ? "لا توجد حجوزات واردة" : "لم تقم بأي حجز بعد"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const isIncoming = r.owner_id === storeId;
            const focused = r.id === focusId;
            return (
              <div key={r.id} id={`reservation-${r.id}`} className={`bg-white border rounded-xl p-3 md:p-4 overflow-hidden ${focused ? "border-[#09637E] ring-2 ring-[#09637E]/40" : "border-[#7AB2B2]"}`} data-testid={`reservation-${r.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex gap-3 min-w-0 flex-1">
                    {r.product_id && (
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden shrink-0">
                        <MerchantPhotoThumb photoId={r.product_id} testid={`res-thumb-${r.id}`} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-extrabold text-[#333333] text-sm md:text-base break-words leading-tight" data-testid={`res-title-${r.id}`}>
                        {r.display_name || r.product_name || "منتج"}
                      </div>
                      {r.product_code && (
                        <div className="text-[11px] font-bold text-[#09637E] mt-0.5" data-testid={`res-code-${r.id}`}>
                          كود المنتج: <span dir="ltr" className="font-mono">{r.product_code}</span>
                        </div>
                      )}
                      <div className="text-xs md:text-sm text-[#666666] mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="truncate">{isIncoming ? `المشتري: ${r.requester_name || "—"}` : `البائع: ${r.owner_name || "—"}`}</span>
                        <StarRating avg={r.other_rating_avg} count={r.other_rating_count} size={11} testid={`other-rating-${r.id}`} />
                      </div>
                      <div className="text-xs md:text-sm text-[#666666]">الكمية: {r.meters} م</div>
                      <div className="text-[#09637E] font-bold text-sm md:text-base" dir="ltr">${(r.total_usd ?? 0).toFixed(2)}</div>
                      {(() => {
                        const own = (a) => a?.actor_name && a.store_id === storeId;
                        const act = own(r.last_actor) ? r.last_actor : own(r.created_by) ? r.created_by : null;
                        if (!act) return null;
                        const label = act === r.created_by && !own(r.last_actor) ? "أنشأ الطلب" : "آخر إجراء";
                        return (
                          <div className="text-[11px] text-[#666666] mt-0.5" data-testid={`res-actor-${r.id}`}>
                            {label}: {act.actor_name} ({act.actor_type === "employee" ? "موظف" : "صاحب المتجر"})
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex sm:flex-col items-start sm:items-end gap-2 flex-wrap sm:flex-nowrap shrink-0">
                    <span className={`text-[11px] md:text-xs font-extrabold px-2.5 py-1 rounded-full ${STATUS_COLOR[r.status] || "bg-gray-100"}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                    <div className="flex gap-1.5 flex-wrap">
                      <Link to={`/messages/${r.id}`} data-testid={`chat-${r.id}`}
                        className="h-8 md:h-9 px-2.5 md:px-3 rounded-lg border-2 border-[#09637E] text-[#09637E] text-[11px] md:text-xs font-bold flex items-center gap-1 hover:bg-[#EBF4F6]">
                        <MessageCircle size={13} /> محادثة
                      </Link>
                      {isIncoming && r.status === "pending" && (
                        <>
                          <button onClick={() => updateStatus(r.id, "confirmed")} data-testid={`confirm-${r.id}`}
                            className="h-8 md:h-9 px-2.5 md:px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] md:text-xs font-bold">تأكيد</button>
                          <button onClick={() => updateStatus(r.id, "cancelled")} data-testid={`cancel-${r.id}`}
                            className="h-8 md:h-9 px-2.5 md:px-3 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[11px] md:text-xs font-bold">إلغاء</button>
                        </>
                      )}
                      {isIncoming && r.status === "confirmed" && (
                        <button onClick={() => updateStatus(r.id, "completed")} data-testid={`complete-${r.id}`}
                          className="h-8 md:h-9 px-2.5 md:px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white text-[11px] md:text-xs font-bold">إتمام البيع</button>
                      )}
                      {r.status === "completed" && !r.my_rating && (
                        <button onClick={() => setRateTarget(r)} data-testid={`rate-${r.id}`}
                          className="h-8 md:h-9 px-2.5 md:px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[11px] md:text-xs font-bold flex items-center gap-1">
                          <Star size={13} /> {isIncoming ? "قيّم المشتري" : "قيّم البائع"}
                        </button>
                      )}
                    </div>
                    {r.status === "completed" && r.my_rating && (
                      <div className="flex items-center gap-1 text-[11px] text-[#666666] font-bold" data-testid={`my-rating-${r.id}`}>
                        تقييمك: <Stars value={r.my_rating} size={12} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rateTarget && (
        <RateModal
          reservationId={rateTarget.id}
          isSeller={rateTarget.owner_id === storeId}
          otherName={rateTarget.owner_id === storeId ? rateTarget.requester_name : rateTarget.owner_name}
          onClose={() => setRateTarget(null)}
          onRated={() => load()}
        />
      )}
    </div>
  );
};

export default Reservations;
